import { faXmark } from '@awesome.me/kit-24322b77ac/icons/classic/solid';
import classNames from 'classnames';
import { keyframes } from 'goober';
import type { JSX, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import type { Toast, ToastOptions, ToastType } from 'react-hot-toast/headless';
import toast, { useToaster } from 'react-hot-toast/headless';

import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';

import './Notification.less';

interface NotificationProps extends ToastOptions {
  message: string;
  onClick?: (event: MouseEvent) => void;
  toPath?: string;
  onKeyDown?: (event: KeyboardEvent) => void;
}

export function successNotification({ message, ...rest }: NotificationProps): string {
  return toast.success(message, { duration: 5000, ...rest });
}

export function infoNotification({ message, ...rest }: NotificationProps): string {
  return toast(message, { duration: 5000, ...rest });
}

export function errorNotification({ message, ...rest }: NotificationProps): string {
  return toast.error(message, { duration: 5000, ...rest });
}

export function warningNotification({ message, ...rest }: NotificationProps): string {
  return toast(message, { duration: 5000, ...rest, className: 'notification-warning' });
}

const enterAnimation = keyframes`
 from {
   opacity: 0;
   transform: translateY(-30px);
 }
 to {
   opacity: 1;
   transform: translateY(0px);
 }
`;

const exitAnimation = keyframes`
 from {
   opacity: 1;
   transform: translateX(0px);
 }
 to {
   opacity: 0;
   transform: translateX(100%);
 }
`;

const typeToClassName = (type: ToastType): string => (type === 'blank' ? '' : `notification-${type}`);

interface NotificationsProps {
  onNavigate?: (path: string) => void;
}

export const Notifications = ({ onNavigate }: NotificationsProps): JSX.Element => {
  const { toasts, handlers } = useToaster();
  const { startPause, endPause, calculateOffset, updateHeight } = handlers;

  return (
    <div
      role="region"
      aria-label="Notifications"
      style={{
        position: 'fixed',
        zIndex: 9999,
        inset: '16px',
        pointerEvents: 'none',
      }}
      onMouseEnter={startPause}
      onMouseLeave={endPause}
    >
      {toasts.map((t: Toast) => {
        const customT = t as Toast & NotificationProps;
        const isInteractive = Boolean(customT.onClick ?? customT.toPath);
        const offset = calculateOffset(t, {
          reverseOrder: false,
        });

        const ref = (element: HTMLDivElement | null): void => {
          if (typeof t.height !== 'number' && element) {
            const height = element.getBoundingClientRect().height;
            updateHeight(t.id, height);
          }
        };

        return (
          <div
            key={t.id}
            className="notification-wrapper"
            style={{
              transform: `translateY(${offset}px)`,
            }}
          >
            <div
              className={classNames('notification', typeToClassName(t.type), t.className)}
              ref={ref}
              style={{
                ...t.style,
                transition: 'all 0.5s ease-out',
                animation: t.visible ? `${enterAnimation} 0.2s ease-out forwards` : `${exitAnimation} 0.4s ease-in forwards`,
              }}
              {...t.ariaProps}
            >
              {isInteractive ? (
                <button
                  type="button"
                  className="notification-message-button"
                  onClick={(event) => {
                    if (customT.toPath && onNavigate) {
                      onNavigate(customT.toPath);
                    }
                    if (customT.onClick) {
                      customT.onClick(event);
                    }
                  }}
                  onKeyDown={customT.onKeyDown}
                >
                  {t.message as ReactNode}
                </button>
              ) : (
                <div>{t.message as ReactNode}</div>
              )}
              {t.type !== 'loading' && (
                <Button
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    toast.dismiss(t.id);
                  }}
                  icon={<Icon style={{ width: 'unset' }} icon={faXmark} />}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
